'use client';

import { useMediaQuery } from '@ui/lib/use-media-query';
import { X } from 'lucide-react';
import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerTitle,
  DrawerTrigger,
} from './drawer';
import { VisuallyHidden } from './visually-hidden';

export interface ResponsiveDrawerDialogProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  title?: string;
  titleIcon?: React.ReactNode;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
  footer?: React.ReactNode;
}

export function ResponsiveDrawerDialog({
  children,
  open,
  onOpenChange,
  trigger,
  title,
  titleIcon,
  description,
  className,
  showCloseButton = false,
  footer,
}: ResponsiveDrawerDialogProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Use controlled state if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          className={`max-h-[90vh] flex flex-col ${className || ''}`}
        >
          {(title || description) && (
            <DialogHeader className="shrink-0">
              {title && (
                <DialogTitle>
                  {titleIcon ? (
                    <div className="flex items-center gap-x-1.5">
                      {titleIcon}
                      <span>{title}</span>
                    </div>
                  ) : (
                    title
                  )}
                </DialogTitle>
              )}
              {description && (
                <DialogDescription>{description}</DialogDescription>
              )}
            </DialogHeader>
          )}
          <div className="flex-1 overflow-y-auto px-6 pb-4">{children}</div>
          {footer && (
            <div className="shrink-0 sticky bottom-0 flex gap-3 bg-white pt-4 pb-2 px-6 shadow-[inset_0_1px_0_rgba(0,0,0,0.1)] [&>*[data-primary='true']]:flex-grow [&>*[data-primary='true']]:min-w-[180px] [&>*[data-primary='true']]:whitespace-normal max-[559px]:flex-col-reverse [&>*]:max-[559px]:w-full max-[559px]:gap-2">
              {footer}
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
      <DrawerContent
        className={`max-h-[90vh] flex flex-col ${className || ''}`}
      >
        {/* Add required accessibility titles - visually hidden when not explicitly provided */}
        {title ? (
          <DrawerTitle>{title}</DrawerTitle>
        ) : (
          <VisuallyHidden asChild>
            <DrawerTitle>Menu</DrawerTitle>
          </VisuallyHidden>
        )}

        {description ? (
          <DrawerDescription>{description}</DrawerDescription>
        ) : (
          <VisuallyHidden asChild>
            <DrawerDescription>Menu options and settings</DrawerDescription>
          </VisuallyHidden>
        )}

        {/* Custom header with drag indicator and close button - no titles */}
        <div className="relative flex justify-center py-2 shrink-0">
          {/* Drag indicator line */}

          {/* Close button in top right */}
          <DrawerClose asChild>
            <button
              type="button"
              className="absolute z-10 right-3 -top-2 rounded-lg p-2 sm:p-2 ring-1 shadow-sm ring-slate-200/80 bg-white ring-offset-background transition-all duration-200 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-800 focus:ring-offset-2 disabled:pointer-events-none touch-manipulation min-h-[32px] sm:min-h-[32px] min-w-[32px] sm:min-w-[auto] flex items-center justify-center"
            >
              <X className="h-4 w-4 sm:h-4 sm:w-4" />
              <span className="sr-only">Close</span>
            </button>
          </DrawerClose>
        </div>

        {/* Visual title header for drawer */}
        {title && (
          <div className="px-6 pb-4 shrink-0">
            {titleIcon ? (
              <div className="flex items-center gap-x-1.5">
                {titleIcon}
                <h2 className="text-lg font-bold leading-none tracking-tight mb-3">
                  {title}
                </h2>
              </div>
            ) : (
              <h2 className="text-lg font-bold leading-none tracking-tight mb-3">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>
        )}

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">{children}</div>

        {footer && (
          <DrawerFooter className="shrink-0 sticky bottom-0 bg-white pt-1.5 pb-1 px-6 shadow-[inset_0_1px_0_rgba(0,0,0,0.1)] [&>*[data-primary='true']]:flex-grow [&>*[data-primary='true']]:min-w-[180px] [&>*[data-primary='true']]:whitespace-normal max-[559px]:flex-col-reverse [&>*]:max-[559px]:w-full max-[559px]:gap-2">
            {footer}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}
